// stage.ts — dual fixed design spaces + a landscape layout frame (FACE&FURY S4 STAGE).
//
// The game boots into ONE of two Scale.FIT design spaces, chosen once by the viewport aspect:
//   - portrait  720×1280..1620 (aspect < 1) — phone stage matched to the boot viewport height.
//   - landscape 1280×800 (aspect ≥ 1)  — a real desktop/landscape stage: the board is centred with a
//     telemetry rail in the left gutter and a command rail in the right gutter; menus/result compose a
//     wide centred card instead of a 450px phone sliver.
// Both run under Scale.FIT + CENTER_BOTH. Portrait height tracks tall phones at boot so the game uses
// the full safe viewport instead of leaving large top/bottom FIT bands.
//
// Renderer-only, DOM-light: no Core/network/determinism touch. Scenes read `isLandscape()` and lay out
// against it directly (MenuScene composes its two panes; GameScene owns the board + gutter-rail geometry
// in computeLayout/buildHud); the fixed design dims come from `scene.scale.width/height`.

export interface DesignSpace {
  /** Fixed Scale.FIT design width (logical px). */
  width: number;
  /** Fixed Scale.FIT design height (logical px). */
  height: number;
  /** true = 1280×800 landscape stage, false = 720×1280 portrait stage. */
  landscape: boolean;
}

/** Baseline portrait phone stage; tall phones get a boot-time height variant. */
export const PORTRAIT: DesignSpace = { width: 720, height: 1280, landscape: false };
/** The desktop / landscape stage. */
export const LANDSCAPE: DesignSpace = { width: 1280, height: 800, landscape: true };

/**
 * Width of EACH side gutter rail on the GameScene landscape stage. Single source of truth shared by
 * boardView.computeLayout (centres the board between the rails) and hudController.buildHud (anchors the
 * left telemetry rail + right command rail). The centre board column is `1280 − 2·GUTTER`.
 */
export const LANDSCAPE_GUTTER_W = 300;

/**
 * Pick the design space from the current viewport aspect. Aspect ≥ 1 (square or wider) → the landscape
 * stage; taller-than-wide → the portrait stage. A phone in portrait, a tablet held either way that is
 * taller than wide, and any portrait PWA get the phone stage; a desktop window, a landscape tablet, and
 * a rotated phone get the landscape stage.
 */
export function chooseDesignSpace(
  vw: number = typeof window === 'undefined' ? 720 : window.innerWidth || 720,
  vh: number = typeof window === 'undefined' ? 1280 : window.innerHeight || 1280,
): DesignSpace {
  if (vw >= vh) return LANDSCAPE;
  const aspectMatchedHeight = Math.round(PORTRAIT.width * (vh / Math.max(1, vw)));
  return {
    ...PORTRAIT,
    height: Math.max(PORTRAIT.height, Math.min(1620, aspectMatchedHeight)),
  };
}

// The design space is fixed for the life of the page (an orientation change that crosses the boundary
// reloads — see main.ts). Default to portrait so any headless/test path that never calls
// setDesignSpace() behaves exactly like the shipped phone build.
let CURRENT: DesignSpace = PORTRAIT;

/** main.ts pins the chosen design space before constructing the Phaser.Game. */
export function setDesignSpace(d: DesignSpace): void {
  CURRENT = d;
}

/** True when the game is running on the 1280×800 landscape stage. */
export function isLandscape(): boolean {
  return CURRENT.landscape;
}
